/**
 * PoLinesSection.jsx - Tracking > Setup > PO lines, the tracking master's
 * INVOICING SUMMARY: the project's PO lines and their values (L4-M15), each invoice of the
 * invoice ledger booked to a line (INVOICE DETAILS' PO #), and per line what
 * is invoiced, what remains and Over / Under Budget - plus the hours and the
 * sold the day rows and materials booked to it (the split of the work by PO
 * line). Projects Edit changes the lines and the bookings; the invoices
 * themselves are kept on the Finance Invoice tab.
 */
import { useEffect, useState } from 'react';
import { Btn, GREEN, MUTED, NumInput, Section, TextInput, td, th } from '../quotes/quoteUi';
import { hours, money, savePoLines, setInvoicePoLine } from '../costs/costApi';
import { Num } from './trackingUi';

export default function PoLinesSection({ data, projectId, editable, setData, onDirty }) {
  const I = data.tracking.invoicing;
  const stored = (data.poLines || []).map(({ label, value }) => ({ label, value }));
  const [lines, setLines] = useState(stored);
  useEffect(() => {
    setLines((data.poLines || []).map(({ label, value }) => ({ label, value })));
  }, [data.poLines]);
  const dirty = JSON.stringify(lines) !== JSON.stringify(stored) || (data.poLines || []).some((l) => l.derived);
  const unsaved = JSON.stringify(lines) !== JSON.stringify(stored);
  useEffect(() => {
    if (onDirty) onDirty(unsaved);
  }, [unsaved, onDirty]);
  const [msg, setMsg] = useState(null);
  const run = async (fn, ok) => {
    const r = await fn();
    if (!r.ok) {
      setMsg({ kind: 'error', text: (r.body && r.body.error) || 'That did not work.' });
      return;
    }
    setMsg({ kind: 'ok', text: ok });
    setData(r.body);
  };
  const set = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const line = (label) => I.lines.find((l) => l.label.toLowerCase() === String(label).toLowerCase()) || {};
  return (
    <Section
      title="PO lines and invoicing"
      hint={
        (data.poLines || []).some((l) => l.derived)
          ? "This project's PO is shown as one line. Split it into its line items and save; then book each invoice to its line."
          : 'Each PO line, what is invoiced against it and what remains, and the hours and sold booked to it (each logged day and material line names its PO line). Book every invoice to its line below.'
      }
      right={
        editable && (
          <span style={{ display: 'flex', gap: 6 }}>
            <Btn small onClick={() => setLines((ls) => [...ls, { label: '', value: '' }])}>
              + PO line
            </Btn>
            <Btn small primary disabled={!dirty} onClick={() => run(() => savePoLines(projectId, lines), 'PO lines saved.')}>
              Save PO lines
            </Btn>
          </span>
        )
      }
    >
      {msg && (
        <div
          role={msg.kind === 'error' ? 'alert' : 'status'}
          style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: msg.kind === 'error' ? '#B71C1C' : GREEN }}
        >
          {msg.text}
        </div>
      )}
      <table aria-label="PO lines" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
        <thead>
          <tr>
            {['PO line', 'PO value', 'Invoiced', 'Remaining', '', 'Hours booked', 'Sold booked', ''].map((x, i) => (
              <th key={`${x}${i}`} style={th}>
                {x}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => {
            const c = line(l.label);
            return (
              <tr key={i}>
                <td style={{ ...td, width: 200 }}>
                  <TextInput aria-label={`PO line ${i + 1}`} value={l.label} disabled={!editable} onChange={(v) => set(i, 'label', v)} />
                </td>
                <td style={{ ...td, width: 120 }}>
                  <NumInput
                    aria-label={`PO line ${i + 1} value`}
                    value={l.value}
                    disabled={!editable}
                    onChange={(v) => set(i, 'value', v)}
                  />
                </td>
                <Num v={money(c.invoiced)} />
                <Num v={money(c.remaining)} color={c.remaining < 0 ? '#B71C1C' : undefined} />
                <td style={{ ...td, fontSize: 10.5, fontWeight: 700, color: c.status === 'Over Budget' ? '#B71C1C' : GREEN }}>
                  {c.status || ''}
                </td>
                <Num v={hours(c.hours)} />
                <Num v={money(c.sell)} />
                <td style={{ ...td, width: 30 }}>
                  {editable && (
                    <Btn small danger aria-label={`Remove PO line ${i + 1}`} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>
                      x
                    </Btn>
                  )}
                </td>
              </tr>
            );
          })}
          <tr style={{ background: '#F6F8F3' }}>
            <td style={{ ...td, fontSize: 12, fontWeight: 800 }}>Total</td>
            <Num v={money(I.poValue)} strong />
            <Num v={money(I.invoiced)} strong />
            <Num v={money(I.open)} strong />
            <td colSpan={4} style={{ ...td, fontSize: 10.5, color: MUTED }}>
              {I.noLine.hours ? `${hours(I.noLine.hours)} hrs are not booked to a PO line.` : ''}
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: GREEN, textTransform: 'uppercase', marginBottom: 4 }}>Invoices</div>
      <table aria-label="Invoices by PO line" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Inv #', 'Date', 'Amount', 'PO line'].map((x) => (
              <th key={x} style={th}>
                {x}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.invoices || []).map((inv) => (
            <tr key={inv.id}>
              <td style={{ ...td, fontSize: 11.5 }}>{inv.number || '-'}</td>
              <td style={{ ...td, fontSize: 11.5 }}>{inv.date || '-'}</td>
              <Num v={money(inv.amount)} />
              <td style={{ ...td, width: 220 }}>
                <select
                  aria-label={`Invoice ${inv.number} PO line`}
                  value={inv.poLine || ''}
                  disabled={!editable}
                  onChange={(e) => run(() => setInvoicePoLine(projectId, inv.id, e.target.value), 'Invoice booked.')}
                  style={{ fontSize: 11.5, padding: '4px 6px', borderRadius: 6, border: '1px solid #E5E9DD', width: '100%' }}
                >
                  <option value="">- not booked -</option>
                  {inv.poLine && !stored.some((l) => l.label === inv.poLine) && <option value={inv.poLine}>{inv.poLine}</option>}
                  {stored.map((l) => (
                    <option key={l.label} value={l.label}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!(data.invoices || []).length && (
        <div style={{ padding: 8, fontSize: 11.5, color: MUTED }}>
          No invoices yet. Invoices are added with Add an invoice on the project's Summary tab, or on the Finance Invoice tab; then book
          each one to its PO line here.
        </div>
      )}
    </Section>
  );
}
